import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";
import { BookingPanel } from "@/components/BookingPanel";
import { Hero } from "@/components/Hero";
import { ActsSection, VerdictSection, PrivateSection } from "@/components/EventSections";

const Paragraphs = ({ text, cls = "text-[#cbbba8]" }) => text.split("\n").filter(Boolean).map((p, i) => <p key={i} className={`mt-5 leading-relaxed ${cls}`}>{p}</p>);

const StoryAndBooking = ({ ev, initialNight }) => (
  <section className="mx-auto grid max-w-6xl gap-10 px-5 py-14 sm:px-8 sm:py-20 lg:grid-cols-[1fr_1.05fr] lg:gap-14" data-testid="toby-section">
    <div>
      {ev.story && (<>
        <p className="eyebrow">{ev.story_title || "The story"}</p>
        <p className="mt-4 font-display text-xl leading-snug text-[#1F1B17] sm:text-2xl lg:text-[1.7rem]">{ev.story}</p>
      </>)}
      {ev.secondary_image && <img src={ev.secondary_image} alt="Toby telling his story" className="mt-8 aspect-[16/10] w-full rounded-xl object-cover shadow-[0_20px_40px_-18px_rgba(40,25,5,0.45)]" />}
    </div>
    <div id="book" className="scroll-mt-24 lg:sticky lg:top-24 lg:self-start">
      <div className="on-dark -mx-5 bg-[#141210] p-5 text-white shadow-[0_30px_60px_-25px_rgba(20,18,16,0.6)] sm:mx-0 sm:rounded-xl sm:p-8">
        <h2 className="font-display text-3xl">{ev.coming_soon ? "Upcoming Dates" : "Reserve Your Seat"}</h2>
        {ev.schedule_note && <p className="mb-8 mt-1 text-sm text-[#BDB3A5]">{ev.schedule_note}</p>}
        <BookingPanel ev={ev} initialNight={initialNight} />
      </div>
    </div>
  </section>
);

const About = ({ ev }) => (
  <section className="bg-[#F1E9DA]" data-testid="about-section">
    <div className="mx-auto max-w-3xl px-5 py-20 text-center">
      <h2 className="font-display text-3xl text-[#1F1B17] sm:text-4xl">{ev.about_title || "About the evening"}</h2>
      <Paragraphs text={ev.about} cls="text-[#4A423A]" />
    </div>
  </section>
);

export default function EventPage() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const [ev, setEv] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    api.get(`/events/${slug}`).then((r) => setEv(r.data)).catch(() => setMissing(true));
  }, [slug]);

  if (missing) return <p className="mx-auto max-w-7xl px-8 py-32 text-[#6B6157]" data-testid="event-not-found">That event couldn't be found.</p>;
  if (!ev) return <Skeleton className="h-[600px] w-full rounded-none" />;

  return (
    <main data-testid="event-page">
      <Hero ev={ev} ctaHref="#book" ctaTestId="event-hero-reserve-button" testid="event-hero" />
      <StoryAndBooking ev={ev} initialNight={params.get("night")} />
      {ev.about && <About ev={ev} />}
      {ev.acts?.length > 0 && <ActsSection acts={ev.acts} />}
      {ev.verdict?.title && <VerdictSection verdict={ev.verdict} />}
      {ev.private_options?.length > 0 && <PrivateSection options={ev.private_options} eventSlug={ev.slug} />}
      {ev.disclaimer && <p className="mx-auto max-w-3xl px-5 py-14 text-center text-sm leading-relaxed text-[#6B6157]" data-testid="event-disclaimer">{ev.disclaimer}</p>}
    </main>
  );
}
